import React, { useState } from "react";
import {
    Box,
    Card,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Menu,
    Skeleton,
    Typography,
    IconButton,
    Tooltip,
} from "@mui/material";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Styles from "../Dashboard.module.scss";

const TaskOverviewTable = ({
    selectedDate,
    handleDateChange,
    taskOverviewData,
    isLoading
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [currentGroup, setCurrentGroup] = useState(0); // 0 for first group, 1 for second group
    const isMenuOpen = Boolean(anchorEl);

    const handleMenuClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const onDateChange = (newDate) => {
        handleDateChange(newDate);
        handleMenuClose();
    };

    const toggleGroup = () => {
        setCurrentGroup((prev) => (prev === 0 ? 1 : 0));
    };

    // Define all issue types with their colors
    const allIssueTypes = [
        { key: 'bug', label: 'Bug', color: '#E53E3E', bgColor: '#FFF5F5', textColor: '#C53030' },
        { key: 'task', label: 'Task', color: '#573AA8', bgColor: '#F4EDFF', textColor: '#573AA8' },
        { key: 'testing', label: 'Testing', color: '#DD6B20', bgColor: '#FFFAF0', textColor: '#C05621' },
        { key: 'designing', label: 'Designing', color: '#D69E2E', bgColor: '#FEFCBF', textColor: '#975A16' },
        { key: 'others', label: 'Others', color: '#718096', bgColor: '#EDF2F7', textColor: '#4A5568' },
        { key: 'meeting', label: 'Meeting', color: '#2B6AB1', bgColor: '#EBF5FF', textColor: '#2B6AB1' },
        { key: 'deployment', label: 'Deployment', color: '#38A169', bgColor: '#F0FFF4', textColor: '#276749' },
    ];

    // Split into two groups: first 3 and remaining 4
    const issueTypeGroups = [
        allIssueTypes.slice(0, 3), // Bug, Task, Testing
        allIssueTypes.slice(3, 7), // Designing, Others, Meeting, Deployment
    ];

    const visibleIssueTypes = issueTypeGroups[currentGroup];

    return (
        <Card className={Styles.TableCard} sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: '16px', boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.05)', border: '1px solid rgba(0,0,0,0.02)' }}>
            {/* Header Section */}
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #F0F0F0', bgcolor: '#fff' }}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, fontSize: "16px", color: "#111827", letterSpacing: '-0.02em' }}>
                        Task Overview
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#6B7280", fontSize: "12px", mt: 0.2 }}>
                        Monitoring project hours & issues
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {/* Toggle Button for Issue Type Groups */}
                    <Button
                        onClick={toggleGroup}
                        variant="contained"
                        size="small"
                        sx={{
                            minWidth: 'auto',
                            borderRadius: '14px',
                            height: '32px',
                            px: 1.5,
                            py: 0,
                            background: 'linear-gradient(135deg, #F0F9FF 0%, #E0F2FE 100%)',
                            border: '1px solid #BAE6FD',
                            color: '#0369A1',
                            boxShadow: '0 1px 2px rgba(3, 105, 161, 0.08)',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #E0F2FE 0%, #BAE6FD 100%)',
                                boxShadow: '0 2px 4px rgba(3, 105, 161, 0.12)',
                                transform: 'translateY(-1px)'
                            },
                            '&:active': {
                                transform: 'translateY(0px)'
                            }
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Box
                                component="span"
                                sx={{
                                    bgcolor: '#0EA5E9',
                                    color: '#FFFFFF',
                                    width: '18px',
                                    height: '18px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '6px',
                                    fontSize: '10px',
                                    fontWeight: 700
                                }}
                            >
                                {currentGroup === 0 ? '3' : '4'}
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    transition: 'transform 0.3s ease',
                                    transform: currentGroup === 0 ? 'rotate(0deg)' : 'rotate(180deg)',
                                    color: '#0369A1'
                                }}
                            >
                                <NavigateNextIcon sx={{ fontSize: 16 }} />
                            </Box>
                        </Box>
                    </Button>

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <div>
                            <Button
                                id="calendar-button"
                                aria-controls={isMenuOpen ? "calendar-menu" : undefined}
                                aria-haspopup="true"
                                aria-expanded={isMenuOpen ? "true" : undefined}
                                onClick={handleMenuClick}
                                startIcon={<KeyboardArrowDownOutlinedIcon sx={{ fontSize: "18px" }} />}
                                sx={{
                                    color: "#4F46E5",
                                    fontSize: "13px",
                                    textTransform: "capitalize",
                                    fontWeight: 700,
                                    bgcolor: "#EEF2FF",
                                    borderRadius: '8px',
                                    px: 1.5,
                                    py: 0.5,
                                    '&:hover': { bgcolor: "#E0E7FF" }
                                }}
                            >
                                {selectedDate.format("MMMM YYYY")}
                            </Button>
                            <Menu
                                id="calendar-menu"
                                anchorEl={anchorEl}
                                open={isMenuOpen}
                                onClose={handleMenuClose}
                                MenuListProps={{
                                    "aria-labelledby": "calendar-button",
                                }}
                                PaperProps={{
                                    sx: { borderRadius: '16px', mt: 1.5, boxShadow: '0px 10px 40px rgba(0,0,0,0.12)', border: '1px solid #f0f0f0' }
                                }}
                            >
                                <Box>
                                    <DateCalendar
                                        views={["month", "year"]}
                                        value={selectedDate}
                                        onChange={onDateChange}
                                    />
                                </Box>
                            </Menu>
                        </div>
                    </LocalizationProvider>
                </Box>
            </Box>

            {/* Scrollable Table Container */}
            <TableContainer sx={{ flexGrow: 1, overflow: 'auto', maxHeight: '250px' }}>
                <Table stickyHeader aria-label="task overview table" sx={{ minWidth: 650 }}>
                    <TableHead sx={{ zIndex: 1000, position: 'sticky', top: 0 }}>
                        <TableRow>
                            <TableCell sx={{ backgroundColor: '#F9FAFB', color: '#4B5563', fontWeight: 700, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em', py: 1.5, pl: 4, borderBottom: '1px solid #E5E7EB', zIndex: 1000 }}>Project</TableCell>
                            {visibleIssueTypes.map((type, index) => (
                                <TableCell
                                    key={type.key}
                                    align="center"
                                    sx={{
                                        backgroundColor: '#F9FAFB',
                                        color: type.color,
                                        fontWeight: 700,
                                        fontSize: '12px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        py: 1.5,
                                        borderBottom: '1px solid #E5E7EB',
                                        zIndex: 1000,
                                        animation: 'fadeSlideIn 0.4s ease-out',
                                        animationDelay: `${index * 0.05}s`,
                                        animationFillMode: 'backwards',
                                        '@keyframes fadeSlideIn': {
                                            '0%': {
                                                opacity: 0,
                                                transform: 'translateX(-10px)'
                                            },
                                            '100%': {
                                                opacity: 1,
                                                transform: 'translateX(0)'
                                            }
                                        }
                                    }}
                                >
                                    {type.label}
                                </TableCell>
                            ))}
                            <TableCell align="center" sx={{ backgroundColor: '#F9FAFB', color: "#059669", fontWeight: 700, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em', py: 1.5, pr: 4, borderBottom: '1px solid #E5E7EB', zIndex: 1000, whiteSpace: 'nowrap' }}>
                                Total Time
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            Array.from(new Array(5)).map((_, index) => (
                                <TableRow key={index}>
                                    {/* Project Name */}
                                    <TableCell sx={{ pl: 4, py: 2, borderBottom: '1px solid #F3F4F6' }}>
                                        <Skeleton
                                            variant="rounded"
                                            width={index % 2 === 0 ? 140 : 110}
                                            height={18}
                                            animation="wave"
                                            sx={{ borderRadius: '6px' }}
                                        />
                                    </TableCell>

                                    {/* Visible Issue Types (Count + Time Badge) */}
                                    {visibleIssueTypes.map((type) => (
                                        <TableCell key={type.key} align="center" sx={{ py: 2, borderBottom: '1px solid #F3F4F6' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                                <Skeleton
                                                    variant="circular"
                                                    width={24}
                                                    height={24}
                                                    animation="wave"
                                                />
                                                <Skeleton
                                                    variant="rounded"
                                                    width={50}
                                                    height={22}
                                                    animation="wave"
                                                    sx={{ borderRadius: '12px' }}
                                                />
                                            </Box>
                                        </TableCell>
                                    ))}

                                    {/* Total Time */}
                                    <TableCell align="center" sx={{ pr: 4, py: 2, borderBottom: '1px solid #F3F4F6' }}>
                                        <Skeleton
                                            variant="rounded"
                                            width={60}
                                            height={24}
                                            animation="wave"
                                            sx={{ mx: 'auto', borderRadius: '12px' }}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : taskOverviewData?.length > 0 ? (
                            (() => {
                                const parseTime = (timeStr) => {
                                    if (!timeStr || typeof timeStr !== 'string') return 0;
                                    const hours = parseInt(timeStr.match(/(\d+)h/)?.[1] || 0);
                                    const minutes = parseInt(timeStr.match(/(\d+)m/)?.[1] || 0);
                                    return hours * 60 + minutes;
                                };

                                const sortedData = [...taskOverviewData].sort((a, b) => {
                                    return parseTime(b.totalTime) - parseTime(a.totalTime);
                                });

                                return sortedData.map((row, index) => (
                                    <TableRow
                                        key={row.projectId}
                                        hover
                                        sx={{
                                            "&:last-child td, &:last-child th": { border: 0 },
                                            transition: 'all 0.2s',
                                            '&:hover': { backgroundColor: '#F8FAFC' }
                                        }}
                                    >
                                        <TableCell
                                            align="left"
                                            sx={{
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                maxWidth: "200px",
                                                fontWeight: 600,
                                                color: '#111827',
                                                py: 1.5,
                                                pl: 4,
                                                borderBottom: '1px solid #F3F4F6',
                                                fontSize: '13px'
                                            }}
                                        >
                                            <Tooltip title={row.projectName} arrow enterTouchDelay={0} leaveTouchDelay={3000}>
                                                <span>{row.projectName}</span>
                                            </Tooltip>
                                        </TableCell>

                                        {/* Visible Issue Types */}
                                        {visibleIssueTypes.map((type, index) => (
                                            <TableCell
                                                key={type.key}
                                                align="center"
                                                sx={{
                                                    py: 1.5,
                                                    borderBottom: '1px solid #F3F4F6',
                                                    animation: 'fadeSlideIn 0.4s ease-out',
                                                    animationDelay: `${index * 0.05}s`,
                                                    animationFillMode: 'backwards',
                                                    '@keyframes fadeSlideIn': {
                                                        '0%': {
                                                            opacity: 0,
                                                            transform: 'translateX(-10px)'
                                                        },
                                                        '100%': {
                                                            opacity: 1,
                                                            transform: 'translateX(0)'
                                                        }
                                                    }
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                                    <Typography variant="body2" fontWeight={700} sx={{ color: type.color, fontSize: '14px' }}>
                                                        {row[type.key] || 0}
                                                    </Typography>
                                                    <Box component="span" sx={{ bgcolor: type.bgColor, color: type.textColor, px: 1, py: 0.25, borderRadius: '12px', fontSize: '0.70rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                                        {row[`${type.key}Time`] || '0h 0m'}
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                        ))}

                                        {/* Total - Emerald Green Theme */}
                                        <TableCell align="center" sx={{ py: 1.5, pr: 4, borderBottom: '1px solid #F3F4F6' }}>
                                            <Box component="span" sx={{ color: '#047857', px: 1.5, py: 0.5, borderRadius: '12px', fontSize: '0.8rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                                                {row.totalTime}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ));
                            })()
                        ) : (
                            <TableRow>
                                <TableCell colSpan={visibleIssueTypes.length + 2} align="center" sx={{ py: 8 }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3 }}>
                                        <Typography variant="h6" color="textSecondary" sx={{ fontWeight: 600, mb: 1 }}>
                                            No Data Found
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            There are no tasks recorded for {selectedDate.format("MMMM YYYY")}.
                                        </Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Card>
    );
};

export default TaskOverviewTable;
